import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useKioskMode } from '../../contexts/KioskModeContext';
import cartService from '../../services/cartService';
import { CART_UPDATED_EVENT } from '../../utils/cartEvents';
import { useGuestOrderType } from '../../hooks/useGuestOrderType';
import { withGuestOrderTypeQuery } from '../../utils/guestOrderTypeStorage';
import { kioskCartPath } from './kioskPaths';
import styles from './KioskFloatingCart.module.css';

const HIDDEN_SUFFIXES = ['/cart', '/login', '/order-type', '/confirmation'];

const KioskFloatingCart: React.FC = () => {
  const { enabled } = useKioskMode();
  const { appName: paramApp } = useParams<{ appName: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const appName = paramApp || null;
  const guestOrderType = useGuestOrderType(appName || undefined);
  const [cartCount, setCartCount] = useState(0);

  const pathHidden = HIDDEN_SUFFIXES.some((suffix) => location.pathname.endsWith(suffix));

  useEffect(() => {
    if (!enabled || !appName) return undefined;
    const refresh = () => {
      cartService
        .getCartCount(appName)
        .then((res) => {
          if (res.success) setCartCount(res.cartCount || 0);
        })
        .catch(() => undefined);
    };
    refresh();
    const handler = (event: Event) => {
      const detail = (event as CustomEvent<{ appName: string; count?: number }>).detail;
      if (detail?.appName !== appName) return;
      if (typeof detail.count === 'number') {
        setCartCount(detail.count);
      } else {
        refresh();
      }
    };
    window.addEventListener(CART_UPDATED_EVENT, handler);
    return () => window.removeEventListener(CART_UPDATED_EVENT, handler);
  }, [enabled, appName]);

  if (!enabled || !appName || pathHidden) {
    return null;
  }

  return (
    <button
      type="button"
      className={`${styles.fab} kiosk-fab-cart`}
      aria-label={cartCount > 0 ? `View cart, ${cartCount} items` : 'View cart'}
      onClick={() => {
        navigate(withGuestOrderTypeQuery(kioskCartPath(appName), appName, guestOrderType));
      }}
    >
      <span className="material-symbols-outlined" aria-hidden>
        shopping_cart
      </span>
      {cartCount > 0 ? <span className={styles.badge}>{cartCount > 99 ? '99+' : cartCount}</span> : null}
    </button>
  );
};

export default KioskFloatingCart;
